import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import parse from "html-react-parser";
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';

// Needed URLS
import { GET_PROGRAMS, ADD_PROGRAM, EDIT_PROGRAM, DELETE_PROGRAM } from '../constants/URLS';

// Needed Components


const ProgramTable = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);

	// CONSTANTS
    const emptyProgram = {
        programID: null,
        title: '',
		subtitle: '',
        credits: '',
        reps: '',
        dateTimeOverride: '',
        speakers: '',
        registerLink: '',
        onDemandLink: '',
		startDate: null,
		endDate: null,
		timeDescription:'',
		locationAlert: '',
		location: ''
    };

	// STATE VARIABLES
    const [programs, setPrograms] = useState([]);
    const [programDialog, setProgramDialog] = useState(false);
    const [deleteProgramDialog, setDeleteProgramDialog] = useState(false);
    const [program, setProgram] = useState(emptyProgram);
	const [credits, setCredits] = useState('');
	const [speakers, setSpeakers] = useState('');
	const [reps, setReps] = useState('');
	const [location, setLocation] = useState('');
	const [timeDescription, setTimeDescription] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [globalFilterValue1, setGlobalFilterValue1] = useState('');
	const [filters1, setFilters1] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
	const [showPastPrograms, setShowPastPrograms] = useState(false);
    
	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGetPrograms()
    }, [showPastPrograms]); 

	useEffect(() => {
		let _program = {...program};
        _program['credits'] = credits;
        setProgram(_program);
    }, [credits]); 

	useEffect(() => {
		let _program = {...program};
        _program['speakers'] = speakers;
        setProgram(_program);
    }, [speakers]); 

	useEffect(() => {
		let _program = {...program};
        _program['timeDescription'] = timeDescription;
        setProgram(_program);
    }, [timeDescription]);
	
	useEffect(() => {
		let _program = {...program};
        _program['reps'] = reps;
        setProgram(_program);
    }, [reps]);
	
	useEffect(() => {
		let _program = {...program};
        _program['location'] = location;
        setProgram(_program);
    }, [location]);

	// ACTION / XHR FUNCTIONS
	const fncGetPrograms = async (data) => {
		
		let strURL = GET_PROGRAMS
		if (showPastPrograms) strURL = GET_PROGRAMS + '?showPast=1'
		
		let _programs = await axios({method: 'get', url: strURL})
		let _sorted = _programs.data.results.map(cur => {
			return {
				...cur,
				startDate: new Date(cur.startDate)
			}
		}).sort((a,b) => (new Date(a.startDate) < new Date(b.startDate)?-1:1)).sort((a,b) => (a.status < b.status?-1:1))
		if (_programs.data.success) setPrograms(_sorted)
		else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: _programs.data.message, life: 3000 });
		}
	};

	const saveProgram = async () => {
        setSubmitted(true);

		let errors = []
		if (!program.title.trim()) errors.push('title')
		if (!program.startDate) errors.push('startDate')
		if (!program.timeDescription) errors.push('timeDescription')
		if (!program.credits) errors.push('credits')
		if (!program.reps) errors.push('reps')
		if (!program.speakers) errors.push('speakers')
		if (!program.registerLink) errors.push('registerLink')

		if (errors.length > 0) {
			return  toast.current.show({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.', life: 3000 });
		}
		
		let _program = {...program};
		if (program.programID) {
			
			let editResults = await axios({
				method: 'post', 
				url: EDIT_PROGRAM,
				data: {..._program}
			})

			if (editResults.data.success) {
				await fncGetPrograms()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Program Updated', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		}
		else {
			let addResults = await axios({
				method: 'post', 
				url: ADD_PROGRAM,
				data: {..._program}
			})
			
			if (addResults.data.success) {
				await fncGetPrograms()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Program Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}
		setSubmitted(false);
		setProgramDialog(false);
		clearProgram()
        
    }

	const deleteProgram = async () => {
        try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_PROGRAM,
				data: {...program}
			})
			if (deleteResults.data.success) {
				let _programs = programs.filter(val => val.programID !== program.programID);
				setPrograms(_programs);
				setDeleteProgramDialog(false);
				clearProgram()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Program Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
    }
	
	// OTHER FUNCTIONS
	const clearProgram = () => {
		setProgram(emptyProgram); 
		setCredits('')
		setSpeakers('')
		setReps('')
		setLocation('')
		setTimeDescription('')
	}

    const openNew = () => {
        clearProgram()
        setSubmitted(false);
        setProgramDialog(true);
    }

    const hideDialog = () => {
        setSubmitted(false);
        setProgramDialog(false);
    }

    const hideDeleteProgramDialog = () => {
        setDeleteProgramDialog(false);
    }

    const editProgram = (program) => {
		setProgram({
			...program,
			startDate: new Date(program.startDate),
			endDate: new Date(program.endDate)
		});
		setCredits(program.credits)
		setSpeakers(program.speakers)
		setTimeDescription(program.timeDescription)
		setReps(program.reps)
		setLocation(program.location)
        setProgramDialog(true);
    }

    const confirmDeleteProgram = (program) => {
        setProgram(program);
        setDeleteProgramDialog(true);
    }

    const findIndexById = (programID) => {
        let index = -1;
        for (let i = 0; i < programs.length; i++) {
            if (programs[i].programID === programID) {
                index = i;
                break;
            }
        }

        return index;
    }

    const onInputChange = (e, name) => {
		const val = (e.target && e.target.value) || '';
        let _program = {...program};
        _program[`${name}`] = val;

        setProgram(_program);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters1 = { ...filters1 };
        _filters1['global'].value = value;

        setFilters1(_filters1);
        setGlobalFilterValue1(value);
    }

	const initFilters1 = () => {
        setFilters1({
            'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
        setGlobalFilterValue1('');
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const htmlTemplate = (rowData, column) => {
        return (
			<div style={{whiteSpace: "pre-wrap"}}>
				{rowData[column.field]}
			</div>
		);
    }

	const titleTemplate = (rowData, column) => {
        return (
			<div className="flex flex-column">
				<div className="programTitle">
					{rowData.title}
				</div>
				<div>
					{rowData.subtitle}
				</div>
				
			</div>
		);
    }

	const dateTemplate = (rowData) => {
        let strStatus = <div className="programStatus active">
			<Tag  severity="success" value="Active" />
		</div>
		if (rowData.status === 2) strStatus = <div className="programStatus archived">
			<Tag  severity="warning" value="Archived" />
		</div>
		if (rowData.status === 3) strStatus = <div className="programStatus old">
			<Tag  severity="danger" value="Old" />
		</div>
		
		return (
			<div className="flex flex-column">
				<div className="pb-2 programTitle flex justify-content-between">
					<div className="flex flex-column">
						<div>
							{dayjs(rowData.startDate).format('MMMM D, YYYY')}
						</div>
						{rowData.endDate && 
							<div>
								{dayjs(rowData.endDate).format('MMMM D, YYYY')}
							</div>
						}
					</div>
					{strStatus}
				</div>
				<div style={{whiteSpace: "pre-wrap"}}>
					{rowData.timeDescription}
				</div>
				{rowData.status === 2 && !rowData.onDemandLink  && 
					<div className="red pt-2">
						*There is no On Demand Link
					</div>
				}
			</div>
		);
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => editProgram(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteProgram(rowData)} />
            </React.Fragment>
        );
    }

	// OTHER TEMPLATES (JSX)
    const leftToolbarTemplate = () => {
        return <Button label="Add New Program" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }

	
				

    const header = (
        <div className="table-header" >
            <h5 className="mx-0 my-1" >Manage Programs</h5>
			<div className="flex flex-column">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText value={globalFilterValue1} onChange={onGlobalFilterChange1} placeholder="Search..." />
				</span>
				<div className="p-input-icon-left field-checkbox mt-2">
					<Checkbox inputId="showPastPrograms" checked={showPastPrograms} onChange={e => setShowPastPrograms(e.checked)} />
					<label htmlFor="showPastPrograms">Show Past Programs</label>
				</div>
			</div>
        </div>
    );

    const programDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveProgram} />
        </>
    );

    const deleteProgramDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteProgramDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteProgram} />
        </>
    );
    
    return (
        <div className="datatable-programs">
            <Toast ref={toast} />

            <div className="card" style={{width:"100%"}}>
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt} value={programs} removableSort 
                    dataKey="programID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} programs"
                    filters={filters1} header={header} responsiveLayout="stack">
                    <Column field="startDate" header="Date" body={dateTemplate} sortable style={{ width: '20%', verticalAlign:'top' }}></Column>
					<Column field="title" header="Title" body={titleTemplate} style={{ width: '15%', verticalAlign:'top' }}></Column>
					<Column field="credits" header="Credits" body={htmlTemplate} style={{ width: '12%', verticalAlign:'top' }}></Column>
					<Column field="reps" header="Rep(s)" body={htmlTemplate} style={{ width: '12%', verticalAlign:'top' }}></Column>
					<Column field="speakers" header="Speaker(s)" body={htmlTemplate} style={{ width: '15%', verticalAlign:'top' }}></Column>
                  	<Column body={actionBodyTemplate} exportable={false} style={{ width: '9%', verticalAlign:'top' }}></Column>
                </DataTable>
            </div>

            <Dialog visible={programDialog} style={{ width: '850px' }} header="Program Details" modal className="p-fluid" footer={programDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column">
					<div className="field">
						<label htmlFor="name">Program Title</label>
						<InputText id="name" value={program.title} onChange={(e) => onInputChange(e, 'title')} required className={classNames({ 'p-invalid': submitted && !program.title })} />
						{submitted && !program.title && <small className="p-error">Title is required.</small>}
					</div>
					<div className="field">
						<label htmlFor="name">Sub-Title (optional)</label>
						<InputText id="name" value={program.subtitle} onChange={(e) => onInputChange(e, 'subtitle')} required />
					</div>
					<div className="flex justify-content-between">
						<div className="field pr-4 halfField">
							<label htmlFor="name">Start Date</label>
							<Calendar id="basic" value={program.startDate} onChange={(e) => onInputChange(e,'startDate')} className={classNames({ 'p-invalid': submitted && !program.startDate })}/>
							{submitted && !program.startDate && <small className="p-error">Start Date is required.</small>}
						</div>
						<div className="field pr-4 halfField">
							<label htmlFor="name">End Date (optional)</label>
							<Calendar id="basic" value={program.endDate} onChange={(e) => onInputChange(e,'endDate')} />
						</div>
					</div>
					<div className="field">
						<label htmlFor="name">Date Description Override (optional)</label>
						<InputText id="name" value={program.dateTimeOverride} onChange={(e) => onInputChange(e, 'dateTimeOverride')} />
					</div>
					
					<div className="flex justify-content-between">
						<div className="field halfField">
							<label htmlFor="time">Program Times</label>
							<InputTextarea style={{height:'120px'}} value={timeDescription} onChange={(e) => setTimeDescription(e.target.value)} />
							{submitted && !timeDescription && <small className="p-error">Program Times are required.</small>}
						</div>
						<div className="field halfField">
							<label htmlFor="credits">Credits</label>
							<InputTextarea style={{height:'120px'}} value={credits} onChange={(e) => setCredits(e.target.value)} />
							{submitted && !credits && <small className="p-error">Credits are required.</small>}
						</div>
					</div>
					
					<div className="flex justify-content-between">
						<div className="field halfField">
							<label htmlFor="name">Rep(s)</label>
							<InputTextarea style={{height:'120px'}} value={reps} onChange={(e) => setReps(e.target.value)} />
							{submitted && !reps && <small className="p-error">Rep(s) are required.</small>}
						</div>
						<div className="field halfField">
							<label htmlFor="credits">Speaker(s)</label>
							<InputTextarea style={{height:'120px'}} value={speakers} onChange={(e) => setSpeakers(e.target.value)} />
							{submitted && !speakers && <small className="p-error">Speaker(s) are required.</small>}
						</div>
					</div>
					
					<div className="field">
						<label htmlFor="name">Location Alert (optional)</label>
						<InputText id="name" value={program.locationAlert} onChange={(e) => onInputChange(e, 'locationAlert')}   />
					</div>

					<div className="field">
						<label htmlFor="credits">Location Description (optional)</label>
						<InputTextarea style={{height:'120px'}} value={location} onChange={(e) => setLocation(e.target.value)} />
						
					</div>

					<div className="field">
						<label htmlFor="name">Link</label>
						<InputText id="name" value={program.registerLink} onChange={(e) => onInputChange(e, 'registerLink')} required className={classNames({ 'p-invalid': submitted && !program.registerLink })}  />
						{submitted && !program.registerLink && <small className="p-error">Link is required.</small>}
					</div>

					<div className="field">
						<label htmlFor="name">On Demand Link (optional)</label>
						<InputText id="name" value={program.onDemandLink} onChange={(e) => onInputChange(e, 'onDemandLink')} />
					</div>
					
					
				</div>
            </Dialog>

            <Dialog visible={deleteProgramDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteProgramDialogFooter} onHide={hideDeleteProgramDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {program && <span>Are you sure you want to delete <b>{program.title}</b>?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default ProgramTable;